import { useCallback } from 'react';
import { useReso } from '../ResoContext';

interface Resource {
  type: 'OpenHouse' | 'Media' | 'Unit' | 'Room' | 'GreenVerification';
  itemNumber?: number;
  itemKey?: number;
}

interface DeleteResult extends Resource {
  success: boolean;
  notFound?: boolean;
  error?: {
    message: string;
    code?: string;
  };
}

function getListingKeyField(entityName: string): string {
  switch (entityName) {
    case 'CWA_GreenVerification':
      return 'CWAGreenVerificationListingKey';
    case 'CWA_Room':
      return 'CWARoomListingKey';
    case 'CWA_Unit':
      return 'CWAUnitTypeListingKey';
    case 'CWA_Media':
      return 'CWAResourceRecordKey';
    case 'CWA_OpenHouse':
      return 'CWAOpenHouseListingKey';
    default:
      throw new Error(`Unknown entity name: ${entityName}`);
  }
}

function getItemNumberField(entityName: string): string {
  switch (entityName) {
    case 'CWA_Room':
      return 'CWARoomItemNumber';
    case 'CWA_Unit':
      return 'CWAUnitItemNumber';
    case 'CWA_Media':
      return 'CWAMediaItemNumber';
    case 'CWA_OpenHouse':
      return 'CWAOpenHouseItemNumber';
    case 'CWA_GreenVerification':
      return '';
    default:
      throw new Error(`Unknown entity name: ${entityName}`);
  }
}

export function getKeyField(entityName: string): string {
  switch (entityName) {
    case `CWA_GreenVerification`:
      return 'CWAGVKey';
    case 'CWA_Room':
      return 'CWAROOMKey';
    case 'CWA_Unit':
      return 'CWAUnitTypeKey';
    case 'CWA_Media':
      return 'CWAMEDVWKey';
    case 'CWA_OpenHouse':
      return 'CWAUOHVWKey';
    default:
      throw new Error(`Unknown entity name: ${entityName}`);
  }
}

export const useDeleteListingResources = () => {
  const { executeQuery } = useReso();

  const deleteResource = useCallback(
    async (
      entityName: string,
      resource: Resource,
      listingKey: string,
    ): Promise<DeleteResult> => {
      try {
        // If we have an itemKey, delete directly
        if (resource.itemKey) {
          await executeQuery(
            `${entityName}('${resource.itemKey.toString()}')`,
            {
              method: 'DELETE',
            },
          );
          return {
            ...resource,
            success: true,
          };
        }

        // If we have an itemNumber, find the corresponding record first
        const listingKeyField = getListingKeyField(entityName);
        const itemNumberField = getItemNumberField(entityName);

        if (!itemNumberField) {
          throw new Error(
            'This resource type does not support item number deletion',
          );
        }

        // Find the resource by item number
        const existingResource: any = await executeQuery(entityName, {
          queryParams: {
            $filter: `${listingKeyField} eq '${listingKey}'`,
            $format: 'json',
          },
        });

        if (!existingResource?.value?.[0]) {
          return {
            ...resource,
            success: false,
            notFound: true,
            error: {
              message: 'Resource not found',
              code: 'NOT_FOUND',
            },
          };
        }

        // Get the key from the found record and delete
        const keyField = getKeyField(entityName);
        const deleteKey = existingResource.value[0][keyField];

        await executeQuery(`${entityName}('${deleteKey.toString()}')`, {
          method: 'DELETE',
        });

        return {
          ...resource,
          success: true,
        };
      } catch (error: any) {
        return {
          ...resource,
          success: false,
          error: {
            message: error.message || 'Unknown error occurred',
            code: error.code,
          },
        };
      }
    },
    [executeQuery],
  );

  const deleteListingResources = useCallback(
    async (
      listingKey: number,
      resources: Resource[],
    ): Promise<DeleteResult[]> => {
      // Process all deletions in parallel
      const results = await Promise.all(
        resources.map((resource) => {
          const entityName = `CWA_${resource.type}`;
          return deleteResource(entityName, resource, listingKey.toString());
        }),
      );

      return results;
    },
    [deleteResource],
  );

  return {
    deleteListingResources,
  };
};
